  import {
    AfterViewInit,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
  } from '@angular/core';
  import Hls from 'hls.js';
  import { ICameraStatus, IModalCamerasModel } from '@types-custom/models/ui/modal-camera.modal';
  import { fromEvent, Observable, Subject, Subscription, takeUntil } from 'rxjs';
  import { LocalStorageManagementService } from '@shared/services/local-storage-management/local-storage-management.service';
  import { HttpErrorResponse } from '@angular/common/http';
  import { CameraService } from '@shared/services/layer-service/camera-service/camera.service';

  /*
  ** Modal Camera
  ** Render a Camera modal that displays a video player with record options.
  */
  @Component({
    selector: 'modal-camera',
    templateUrl: './modal-camera.component.html',
    styleUrls: ['./modal-camera.component.scss'],
  })
  export class ModalCameraComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() properties: IModalCamerasModel | undefined;
    @ViewChild('videoPlayer', { static: false }) playerContainer!: ElementRef<HTMLElement>;

    errorMessage = '';
    fatalError = false;
    color = '';
    private urlSource = '';
    private isStreamingPlayer = true;
    private subscription = new Subscription();
    private hls: Hls = null;
    private retryCount = 0;
    private maxRetries = 5;

    private videoElement: HTMLVideoElement | null = null;

    private timerRef: any;
    private stop$ = new Subject<void>();
    imgTest = '';


    private getErrorMessageForStatusCode(statusCode: number): string {
      switch (statusCode) {
        case 400: return 'Hubo un problema con la petición.';
        case 401: return 'No tienes permisos para acceder a este contenido.';
        case 403: return 'No tienes permisos para acceder a este contenido.';
        case 404: return 'No se ha encontrado el contenido solicitado.';
        case 500: return 'Ha ocurrido un error en el servidor.';
        default: return 'Fallo inesperado, reintenta conectar.';
      }
    }
    constructor(private localStorageManagementService: LocalStorageManagementService) { }

    ngOnInit() {
      this.initializeStreamingPlayer();
      this.setMessage('Iniciando la cámara.', 1)
    }


    ngAfterViewInit() {
      this.videoElement = this.getVideoElement();
      this.properties.cameraStatus?.subscribe({
        // next: (val: ICameraStatus) => {
        next: () => {
          this.initializeVideoPlayer();
          this.subscribeToVideoEvents();
          this.initializeStartLoop(() => this.properties.cameraStatusStart(this.properties.title))
        },
        error: (err: HttpErrorResponse) => {
          this.setMessage(this.getErrorMessageForStatusCode(err.status), 3)
        }
      });
    }

    private initializeStartLoop(callback: () => Observable<ICameraStatus>) {
      this.timerRef = setTimeout(() => {
        callback()
          .pipe(takeUntil(this.stop$))
          .subscribe();
        this.initializeStartLoop(callback);
      }, 300000); //5:00
      // }, 5000); //00:05
    }

    private getVideoElement(): HTMLVideoElement | null {
      return this.playerContainer.nativeElement.querySelector('video');
    }

    ngOnDestroy() {
      this.cleanupResources();
      this.stop$.next();
      this.stop$.complete();
      clearTimeout(this.timerRef);
    }

    private initializeStreamingPlayer() {
      this.isStreamingPlayer = this.properties?.streamPlayer ?? false;
      const playbackController = this.properties?.playBackControl;
      if (playbackController) {
        this.urlSource = this.isStreamingPlayer
          ? `${playbackController.streamUrl}/${this.properties.title}.m3u8`
          : playbackController.videoUrl;
      }
    }

    private initializeVideoPlayer() {
      if (!this.videoElement) return;

      if (Hls.isSupported()) {
        this.setupHls(this.videoElement);
      } else if (this.videoElement.canPlayType("application/vnd.apple.mpegurl")) {
        this.videoElement.src = this.urlSource;
      } else {
        alert("No se puede reproducir el contenido en tu navegador");
      }
    }

    private setMessage(message: string = '', level: number = 0) {
      this.errorMessage = message;
      const colors = [
        '#C6FF01',
        'yellow',
        'orange',
        'red',
      ];
      if (level === 3) this.fatalError = true;
      this.color = colors[level];
    }

    private setupHls(video: HTMLVideoElement) {
      this.hls = new Hls({
        xhrSetup: (xhr: any) => {
          xhr.setRequestHeader("Authorization", `Bearer ${this.localStorageManagementService.getUserLocalStorage()}`);
        },
        backBufferLength: 0, // Longitud del buffer almacenado en segundos
        maxBufferHole: 2, // Longitud máxima de espacio entre segmentos en segundos
        nudgeMaxRetry: 10, // Número máximo de reintentos de nudge
        liveDurationInfinity: true,
        fragLoadingMaxRetry: 10, // Número máximo de reintentos de carga de fragmentos
        fragLoadingRetryDelay: 5000, // Retraso inicial para reintentar la carga de fragmentos
        manifestLoadingMaxRetry: 5, // Número máximo de reintentos de carga del manifiesto
        manifestLoadingRetryDelay: 2500, // Retraso inicial para reintentar la carga del manifiesto
      });

      this.hls.on(Hls.Events.ERROR, (_ev: any, data: any) => {
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          const status = data?.response;
          if (status && status.code) {
            this.setMessage(this.getErrorMessageForStatusCode(status.code), 3)
          }
          this.handleVideoError();
        }
      });

      this.hls.loadSource(this.urlSource);
      this.hls.attachMedia(video);
    }

    private subscribeToVideoEvents() {
      if (!this.videoElement) return;
      this.subscription.add(fromEvent(this.videoElement, 'loadstart').subscribe(() => this.setMessage('Conectando, por favor espere', 0)));

      this.subscription.add(fromEvent(this.videoElement, 'error').subscribe(() => {
        this.setMessage('La conexión ha fallado, reintentando conectar', 3);
      }));
      this.subscription.add(fromEvent(this.videoElement, 'stalled').subscribe(() => {
        this.setMessage('La conexión se encuentra estancada, reintentando', 3);
      }));
      this.subscription.add(fromEvent(this.videoElement, 'waiting').subscribe(() => this.setMessage('Conexión inestable, reconectando', 2)));

      this.subscription.add(fromEvent(this.videoElement, 'abort').subscribe(() => this.setMessage('La conexión ha sido abortada.')));
      this.subscription.add(fromEvent(this.videoElement, 'playing').subscribe(() => this.setMessage()));
    }



    private handleVideoError() {
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        this.errorMessage = 'Reintentando conexión... ' + this.retryCount + ' de ' + this.maxRetries + ' intentos.';
        setTimeout(() => {
          this.reloadVideo();
        }, 2000); // Retraso de 2 segundos antes de reintentar
      } else {
        this.fatalError = true;
        this.setMessage('Hubo un fallo en el video.\nPor favor, inténtalo más tarde.', 3);
      }
    }


    private reloadVideo() {
      console.warn("Reloading video...");
      let video = this.getVideoElement();
      if (!video) return;

      video.removeAttribute('src');
      video.load();

      if (this.hls) {
        this.hls.destroy();
        this.hls = null;
      }
      video = video || this.getVideoElement();
      this.setupHls(video);
      video.load();
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.then(() => {
          this.setMessage();
        }).catch((err) => {
          this.setMessage('No se puede reproducir el contenido en tu navegador', 3);
        });
      }
    }

    private cleanupResources() {
      this.subscription.unsubscribe();
      this.closeStreamingConnection();

      if (this.hls) {
        this.hls.destroy();
        this.hls = null;
      }
    }

    private closeStreamingConnection() {
      const video = this.getVideoElement();
      if (!video) return;

      video.pause();
      video.removeAttribute('src');
      video.load();
      video.parentElement.removeChild(video);
    }
  }
